/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class SavedPromptsImpl
 *
 */
define([
	'../SavedPromptsAPI',
	'./SavedPromptsAPISpec',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../lib/@waca/dashboard-common/dist/utils/CommonUtil',
	'underscore'
], (SavedPromptsAPI, SavedPromptsAPISpec, APIFactory, CommonUtil, _) => {

	const NUMERIC_PROMPT_DATA_TYPES_LIST = ['xsdInt', 'xsdLong', 'xsdShort', 'xsdFloat', 'xsdDecimal', 'xsdDouble'];
	// TODO xqe should return the same data type as the model filter for global parameters.
	// Add a temporary map to get a corresponding model prompt dataType.
	const getMappedDataType = sourceType => {
		switch(sourceType) {
			case 'integer': return 'xsdInt';
			case 'long': return 'xsdLong';
			case 'short': return 'xsdShort';
			case 'float': return 'xsdFloat';
			case 'decimal': return 'xsdDecimal';
			case 'double': return 'xsdDouble';
			case 'datetime': return 'xsdDateTime';
			case 'date': return 'xsdDate';
			case 'time': return 'xsdTime';
			default: return 'xsdString';
		}
	};

	/** Class representing a SavedPrompts Implementation */
	class SavedPromptsImpl extends SavedPromptsAPISpec {
		/**
		 * Create a SavedPromptsImpl of SavedPromtsAPI
		 * @param widget the corresponding widget.
		 */
		constructor(widgetModel) {
			super();
			this.widgetModel = widgetModel;
		}

		destroy() {
			this.widgetModel = null;
		}

		getAPI(){
			return APIFactory.createAPI(this, [ SavedPromptsAPI ]);
		}

		save(newValueSpec) {
			const specToSave = _.pick(newValueSpec, SavedPromptsImpl.PromptValueSpec_KEYS);
			const savedPrompts = this.widgetModel.get('savedPrompts') || {};
			savedPrompts[specToSave.name] = specToSave;
			return this.reset(savedPrompts);
		}

		reset(newValueSpec, refresh = false) {
			//silently change the model. an api event will be triggered for this method.
			this.widgetModel['savedPrompts'] = newValueSpec;
			return refresh;
		}

		getPromptSpec(promptName) {
			const savedPrompts = this.widgetModel.get('savedPrompts');
			if (!savedPrompts || (promptName && !savedPrompts[promptName])) {
				return undefined;
			} else {
				const savedPromptsCopy = JSON.parse(JSON.stringify(savedPrompts));
				return savedPromptsCopy[promptName];
			}
		}

		getPromptSpecList() {
			const savedPrompts =  this.widgetModel.get('savedPrompts') || {};
			const savedPromptsCopy = JSON.parse(JSON.stringify(savedPrompts));
			return _.values(savedPromptsCopy);
		}

		_toLocalAttrs(globalParamSpec) {
			if (!this._isRangePrompt(globalParamSpec)) {
				globalParamSpec.values && globalParamSpec.values.forEach(valueObj => {
					if (valueObj.value === undefined) {
						// '.use' is the value to be used to replace the parameter values in filter expression in xqe.
						// It's not query result '.u' or '.d'.
						Object.assign(valueObj, { value: valueObj.use, label: valueObj.use });
						delete valueObj.use;
						delete valueObj.display;
					}
				});
			} else if (!this._isNumericRangePrompt(globalParamSpec)) {
				globalParamSpec.values = this._getRangePromptValues(globalParamSpec.values[0]);
			} else {
				globalParamSpec.values = this._getRangePromptValues(globalParamSpec.values[0], true);
			}
		}

		_getRangePromptValues(valueObj, isNumericRangePrompt = false) {
			if (!isNumericRangePrompt) {
				return [{ value: valueObj.use, label: valueObj.use }, { value: valueObj.use2, label: valueObj.use2 }];
			} else {
				return [{ value: parseFloat(valueObj.use), label: valueObj.use }, { value: parseFloat(valueObj.use2), label: valueObj.use2 }];
			}
		}

		_isRangePrompt(globalParamSpec) {
			return globalParamSpec.values && globalParamSpec.values[0] && !globalParamSpec.values[0].value &&
				globalParamSpec.values[0].use && globalParamSpec.values[0].use2;
		}

		_isNumericRangePrompt(globalParamSpec) {
			return this._isRangePrompt(globalParamSpec) &&
				NUMERIC_PROMPT_DATA_TYPES_LIST.includes(globalParamSpec.dataType);
		}

		reCalculateAndSetSavedPrompts(usedParameters, reCalculateOnlyGlobalParams = false) {
			let newSpecs = {};
			//Update global params always
			this._reCalculateAndSetGlobalSavedPrompts(usedParameters, newSpecs);
			//Update local parameters or just add saved local parameters to spec
			if (reCalculateOnlyGlobalParams) {
				this._addLocalSavedPrompts(newSpecs);
			} else {
				this._reCalculateAndSetLocalSavedPrompts(usedParameters, newSpecs);
			}
			//Always add saved optional prompts
			this._addOptionalSavedPrompts(newSpecs);
			// Reset to null if prompts are all deleted, otherwise reset to new values.
			newSpecs = !_.isEmpty(newSpecs) ? newSpecs : null;
			this.reset(newSpecs);
		}

		_reCalculateAndSetGlobalSavedPrompts(usedParameters, newSpecs) {
			if (usedParameters.length) {
				// Need to get the original prompt spec for member ids to uniquely identify selected members
				usedParameters.forEach(usedParamSpec => {
					let savedSpec =  this.getPromptSpec(usedParamSpec.parameterName);
					if (usedParamSpec.location === SavedPromptsImpl.globalParameterName || (savedSpec && savedSpec.location === SavedPromptsImpl.globalParameterName)) {
						// Global parameter is used in query so no prompt fault returned for local prompt with the same name.
						// Initialize and save a local prompt with global parameter spec.
						// This will allow user to re-edit the local prompt with provided modelFilterItem.
						// values undefined, when global parameter values are not selected, remove saved prompt for global parameter, if exist.
						if (usedParamSpec.values) {
							usedParamSpec.name = usedParamSpec.parameterName;
							// this mapping will be removed after xqe return the same data type as the model filter
							usedParamSpec.dataType = getMappedDataType(usedParamSpec.dataType);
							this._toLocalAttrs(usedParamSpec);
							newSpecs[usedParamSpec.name] = usedParamSpec;
						}
					}
				});
			}
		}

		_reCalculateAndSetLocalSavedPrompts(usedParameters, newSpecs) {
			// Recalculate local saved prompts
			if (usedParameters.length) {
				usedParameters.forEach(usedParamSpec => {
					let savedSpec = this.getPromptSpec(usedParamSpec.parameterName);
					if (savedSpec && savedSpec.location !== SavedPromptsImpl.globalParameterName) {
						newSpecs[usedParamSpec.parameterName] = this._updateSavedSpecValuesFromLatestResponse(usedParamSpec, savedSpec);
					}
				});
			}
		}

		/**
		 * usedParameter information is returned as part of the metadata section of the query response
		 * There's a chance that this has changed (eg: a model expression, capabilities or types might have been updated).
		 * Make sure our model stays in sync with the latest.
		 * This also addresses a bug where older prompt faults didn't include the qualified ColumnId (see CADBLW-9798)
		 * NOTE: In future, we may want to tie updates made here to a notification message that the parameter information was updated.
		 * @param {Object} usedParamSpec - a specification of the parameters that were used in the request (in query format)
		 * @param {Object} savedSpec - the specification that is persisted in our model.
		 * @returns An up-to-date spec for saving with the pieces we're interested in persisting in our format.
		 */
		_updateSavedSpecValuesFromLatestResponse(usedParamSpec, savedSpec) {
			const finalSavedSpec = CommonUtil.deepClone(savedSpec);
			const latestResponse = _.pick(usedParamSpec, SavedPromptsImpl.PromptValueSpec_KEYS);
			latestResponse.dataType = getMappedDataType(usedParamSpec.dataType);
			this._toLocalAttrs(latestResponse);
			SavedPromptsImpl.PromptValueSpec_KEYS.forEach((key) => {
				if (latestResponse[key] && !_.isEqual(latestResponse[key], savedSpec[key])) {
					finalSavedSpec[key]=latestResponse[key];
				}
			});
			return finalSavedSpec;
		}

		_addOptionalSavedPrompts(newSpecs) {
			// Add the optional prompts which are not included as used params
			const optionalPrompts = this.getPromptSpecList().filter(spec => spec.capabilities.optional);
			if (optionalPrompts.length) {
				optionalPrompts.forEach(prompt => {
					newSpecs[prompt.name] = prompt;
				});
			}
		}

		_addLocalSavedPrompts(newSpecs) {
			const localPrompts = this.getPromptSpecList().filter(spec => !spec.capabilities.optional && spec.location !== SavedPromptsImpl.globalParameterName);
			// Add the optional prompts which are not included as used params
			if (localPrompts.length) {
				localPrompts.forEach(prompt => {
					newSpecs[prompt.name] = prompt;
				});
			}
		}
	}

	/**
	 * Enum for PROMPTVALUE_KEY.
	 * @readonly
	 * @enum {string}
	 */
	SavedPromptsImpl.PromptValueSpec_KEYS = ['name', 'capabilities', 'dataType', 'values', 'modelFilterItem'];

	SavedPromptsImpl.globalParameterName = 'global';

	return SavedPromptsImpl;
});
